//latest dashboard


import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useGuestSignupModal } from '@/features/pre-login/context/GuestSignupModalContext';

import {useWelcomeModal} from "@/features/dashboard/care-seeker/hooks/useWelcomeModal";
import { CarePlansSkeleton } from '@/features/resource-hub';


// import {WelcomeLetter} from './WelcomeLetter';
import {WelcomeLetter} from "@/features/dashboard/care-seeker/components/WelcomeLetter";

import { FileText, Plus, Clock } from 'lucide-react';

import { Button } from '@/shared/components/ui/button';

// import { useCarePlans } from '../../hooks/useCarePlans';
import { useCarePlans } from '@/features/dashboard/care-seeker/hooks/useCarePlans';
import { useActiveSession } from '@/features/dashboard/care-seeker/hooks/useActiveSession';
import type { CarePlan } from '@/features/dashboard/care-seeker/api/carePlansApi';

const formatCarePlanDate = (createdAt: string) => {
  const datePart = createdAt.match(/^\d{4}-\d{2}-\d{2}/)?.[0];

  const date = datePart ? new Date(`${datePart}T00:00:00Z`) : new Date(createdAt);

  if (Number.isNaN(date.getTime())) {
    return createdAt;
  }

  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

export default function SimplifiedDashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { openModal: openGuestSignupModal } = useGuestSignupModal();
  const { showModal, dismissModal } = useWelcomeModal();

  const [showWelcomeLetter, setShowWelcomeLetter] = useState(false);
  const [showAllPlans, setShowAllPlans] = useState(false);

  // "My Care Plans" is an account-only, cross-session list (keyed by owner). Guests
  // have no account to scope by, so skip the request and show a signup prompt.
  const { data: carePlans = [], isLoading } = useCarePlans({ enabled: isAuthenticated });
  const { data: activeSession, isLoading: isLoadingActiveSession } = useActiveSession({
    enabled: isAuthenticated,
  });

  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'there';



  const handleBeginNow = () => {
    localStorage.removeItem('chat_session_id');
    sessionStorage.setItem('force_new_chat', 'true');
    sessionStorage.removeItem('bquest_nav_session_id');

    Object.keys(sessionStorage)
      .filter((k) => k.startsWith('bquest_journey_summary_'))
      .forEach((k) => sessionStorage.removeItem(k));

    navigate('/redesign-chatbot');
  };

  const handleContinuePlan = () => {
    if (!activeSession) return;
    const { session_id, status } = activeSession;

    sessionStorage.removeItem('force_new_chat');
    sessionStorage.setItem('bquest_nav_session_id', session_id);

    if (status === 'IN_PROGRESS') {
      localStorage.setItem('chat_session_id', session_id);
      navigate('/redesign-chatbot');
      return;
    }
    if (status === 'STAGE_DETECTED') {
      navigate('/redesign-chatbot/situation-summary', { state: { sessionId: session_id } });
      return;
    }
    if (status === 'CONCIERGE_ROUTED') {
      navigate('/redesign-chatbot/concierge');
      return;
    }
    // MATCHMAKING and PLAN_GENERATED both resume on the matching screen —
    // /find-providers/status already guards against re-running the matchmaking pipeline.
    navigate('/redesign-chatbot/matching/0', { state: { sessionId: session_id } });
  };



  if (showModal) {
    return <WelcomeLetter onClose={dismissModal} />;
  }

  if (showWelcomeLetter) {
    return <WelcomeLetter onClose={() => setShowWelcomeLetter(false)} />;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-gray-900 sm:text-4xl">
            Welcome back, {displayName}
          </h1>
          <p className="mt-3 text-base text-gray-700 sm:text-lg">
            Continue your care planning journey or start a new plan
          </p>
        </div>

        {/* Care Plan Actions */}
        <div className="mb-8 grid gap-5 lg:grid-cols-2">
          <div className="rounded-2xl bg-[#31786F] p-6 text-white sm:p-8">
            <div className="flex flex-col gap-5 sm:flex-row">
              <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-white/20">
                <Plus size={30} strokeWidth={2.25} />
              </div>
              <div className="min-w-0">
                <h2 className="text-2xl font-semibold">Start a New Care Plan</h2>
                <p className="mt-4 max-w-md text-base leading-7 text-white">
                  Our AI Navigator will guide you through creating a personalized care plan
                </p>
                <Button
                  onClick={handleBeginNow}
                  className="mt-5 bg-white text-[#27675F] hover:bg-white"
                >
                  Begin Now
                </Button>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="flex flex-col gap-5 sm:flex-row">
              <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-[#DCEBFF]">
                <FileText className="text-[#31786F]" size={30} strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <h2 className="text-2xl font-semibold text-gray-900">Continue Your Plan</h2>
                <p className="mt-4 text-base leading-7 text-gray-700">
                  Resume working on your most recent care plan
                </p>
                <Button
                  onClick={handleContinuePlan}
                  disabled={isLoadingActiveSession || !activeSession}
                  variant="outline"
                  className="mt-5 border-[#31786F] text-[#27675F] hover:bg-white disabled:opacity-40"
                >
                  View Plan
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Your Care Plans */}
        <div className="mt-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">Your Care Plans</h2>

          {!isAuthenticated ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
              <p className="text-lg font-medium text-gray-900">Save your care plan</p>
              <p className="mt-2 text-sm text-gray-500">
                Create a free account to save your plan and revisit it anytime.
              </p>
              <Button
                onClick={() =>
                  openGuestSignupModal({ trigger: 'action_button_click', triggerEntityId: 'care_plans_list' })
                }
                className="mt-5 bg-[#31786F] text-white hover:bg-[#27675F]"
              >
                Create Account
              </Button>
            </div>
          ) : isLoading ? (
            <CarePlansSkeleton />
          ) : carePlans.length > 0 ? (
            <>
              <div className="flex flex-col gap-3">
                {(showAllPlans ? carePlans : carePlans.slice(0, 5)).map((plan: CarePlan) => (
                  <div
                    key={plan.id}
                    className="rounded-2xl border border-gray-200 bg-white px-5 py-6 sm:px-8"
                  >
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-xl text-gray-900">{plan.plan_name}</p>

                        {plan.plan_description && (
                          <p className="mt-3 line-clamp-2 text-base text-gray-700">
                            {plan.plan_description}
                          </p>
                        )}

                        <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                          <Clock size={16} />
                          <span>Created {formatCarePlanDate(plan.created_at)}</span>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => navigate('/redesign-chatbot/plan', { state: { sessionId: plan.session_id } })}
                        disabled={!plan.session_id}
                        className="self-start text-sm font-semibold text-[#27675F] disabled:opacity-40"
                      >
                        View &rarr;
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {carePlans.length > 5 && !showAllPlans && (
                <div className="mt-5 flex justify-end">
                  <Button
                    onClick={() => setShowAllPlans(true)}
                    className="bg-[#31786F] text-white hover:bg-[#27675F]"
                  >
                    View More
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
              <p className="text-lg font-medium text-gray-900">No Care Plans Found</p>
              <p className="mt-2 text-sm text-gray-500">Start a new care plan to see it here.</p>
            </div>
          )}
        </div>

        {/* Feature 017: Need Help card moved below primary features */}

        {/* Feature 014: My Requests Section */}

        {/* View Welcome Letter and Platform Overview Buttons */}
      </div>

      {/* Modals */}
      {/* <OnboardingModal
        open={showOnboarding || showOnboardingManual}
        onClose={() => {
          dismissOnboarding();
          setShowOnboardingManual(false);
        }}
      />
      <SmartSearchPlaceholder
        open={showPlaceholder}
        onClose={() => setShowPlaceholder(false)}
      />
      <CareConciergeModal
        open={showCareConciergeModal}
        onClose={() => setShowCareConciergeModal(false)}
      /> */}
    </div>
  );
}
